// store/slices/clientsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getClients,
  createClient,
  updateClient,
  deleteClient,
  getClientMovements,
  createMovement,
} from "../../utils/accountApi";
import { logoutUser } from "./usersSlice";

/* ================= INITIAL STATE ================= */

const initialState = {
  clientsAdmin: [],
  clientDetail: {},
  clientMovements: [],
  clientMovementsBalance: 0,
  loading: false,
  error: null,
};

/* ================= THUNKS ================= */

export const fetchClientsAdmin = createAsyncThunk(
  "clients/fetchAll",
  async (options = {}, { rejectWithValue }) => {
    try {
      const params = options && options.all ? { all: "true" } : {};
      const { data } = await getClients(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const addClient = createAsyncThunk(
  "clients/add",
  async (client, { rejectWithValue }) => {
    try {
      const { data } = await createClient(client);
      return data.client;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updClient = createAsyncThunk(
  "clients/update",
  async ({ id, client }, { rejectWithValue }) => {
    try {
      const { data } = await updateClient(id, client);
      return data.client;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const delClient = createAsyncThunk(
  "clients/delete",
  async (id, { rejectWithValue }) => {
    try {
      await deleteClient(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchClientMovements = createAsyncThunk(
  "clients/fetchMovements",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getClientMovements(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const addClientMovement = createAsyncThunk(
  "clients/addMovement",
  async ({ id, movement }, { rejectWithValue }) => {
    try {
      const { data } = await createMovement(id, movement);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

/* ================= SLICE ================= */

const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    clearClientError(state) {
      state.error = null;
    },
    resetClientMovements(state) {
      state.clientDetail = {};
      state.clientMovements = [];
      state.clientMovementsBalance = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ===== LISTADO ===== */
      .addCase(fetchClientsAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientsAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.clientsAdmin = action.payload;
      })
      .addCase(fetchClientsAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== ALTA ===== */
      .addCase(addClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clientsAdmin.push(action.payload);
        localStorage.setItem("clientAdded", true);
      })
      .addCase(addClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        localStorage.setItem("clientAdded", action.payload);
      })

      /* ===== MODIFICACIÓN ===== */
      .addCase(updClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updClient.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index = state.clientsAdmin.findIndex((c) => c.id === updated.id);
        if (index >= 0) state.clientsAdmin[index] = updated;
        if (state.clientDetail && state.clientDetail.id === updated.id) {
          state.clientDetail = updated;
        }
        localStorage.setItem("clientUpdated", true);
      })
      .addCase(updClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        localStorage.setItem("clientUpdated", action.payload);
      })

      /* ===== BORRADO LÓGICO ===== */
      .addCase(delClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(delClient.fulfilled, (state, action) => {
        state.loading = false;
        state.clientsAdmin = state.clientsAdmin.filter(
          (c) => c.id !== action.payload
        );
        localStorage.setItem("clientDeleted", true);
      })
      .addCase(delClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        localStorage.setItem("clientDeleted", action.payload);
      })

      /* ===== MOVIMIENTOS (ADMIN) ===== */
      .addCase(fetchClientMovements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientMovements.fulfilled, (state, action) => {
        state.loading = false;
        state.clientDetail = action.payload.client;
        state.clientMovements = action.payload.movements;
        state.clientMovementsBalance = action.payload.balance;
      })
      .addCase(fetchClientMovements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== ALTA DE MOVIMIENTO ===== */
      .addCase(addClientMovement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addClientMovement.fulfilled, (state, action) => {
        state.loading = false;
        const clientId = action.meta.arg.id;
        const { movement, balance } = action.payload;

        state.clientMovements = [movement, ...state.clientMovements];
        state.clientMovementsBalance = balance;

        const index = state.clientsAdmin.findIndex((c) => c.id === clientId);
        if (index >= 0) state.clientsAdmin[index].balance = balance;

        localStorage.setItem("movementAdded", true);
      })
      .addCase(addClientMovement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        localStorage.setItem("movementAdded", action.payload);
      })

      /* ===== LOGOUT ===== */
      .addCase(logoutUser, () => initialState);
  },
});

export const { clearClientError, resetClientMovements } =
  clientsSlice.actions;
export default clientsSlice.reducer;
